var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useState, useRef, useCallback, useEffect, useMemo, } from "react";
import { Layout, Menu, Breadcrumb, Button, Space } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import { COMPONENT_OUTLINE_BORDER, COMPONENT_SPLIT_BORDER_BOTTOM, } from "./theme/componentBorders";
var Sider = Layout.Sider, Content = Layout.Content;
var MainLayout = function (_a) {
    var config = _a.config, children = _a.children;
    var system = config.system, menuItems = config.menuItems, providedBreadcrumbs = config.breadcrumbs, headerActions = config.headerActions, headerExtra = config.headerExtra, styles = config.styles;
    var location = useLocation();
    var navigate = useNavigate();
    // 边栏拖拽相关状态
    var _b = useState(200), siderWidth = _b[0], setSiderWidth = _b[1];
    var _c = useState(false), isResizing = _c[0], setIsResizing = _c[1];
    var isResizingRef = useRef(false);
    var startXRef = useRef(0);
    var startWidthRef = useRef(200);
    // 边栏宽度限制
    var MIN_SIDER_WIDTH = 160;
    var MAX_SIDER_WIDTH = 300;
    // 递归查找匹配的菜单项（包括子菜单）
    var findMatchingMenuItem = useCallback(function (items, path) {
        for (var _i = 0, items_1 = items; _i < items_1.length; _i++) {
            var item = items_1[_i];
            if (item.path) {
                // 精确匹配
                if (item.path === path) {
                    return item;
                }
                // 支持子路径匹配
                if (path.startsWith(item.path + "/")) {
                    return item;
                }
            }
            // 递归查找子菜单
            if (item.children && item.children.length > 0) {
                var childMatch = findMatchingMenuItem(item.children, path);
                if (childMatch) {
                    return childMatch;
                }
            }
        }
        return null;
    }, []);
    // 根据当前路径获取对应的菜单key（包括子菜单）
    var getSelectedMenuKeys = useMemo(function () {
        var _a;
        var path = location.pathname;
        if (!menuItems || !Array.isArray(menuItems) || menuItems.length === 0) {
            return [];
        }
        var matchingItem = findMatchingMenuItem(menuItems, path);
        if (matchingItem) {
            // 返回匹配项及其所有父项的key
            var keys = [];
            var findPath_1 = function (items, target, path) {
                if (path === void 0) { path = []; }
                for (var _i = 0, items_2 = items; _i < items_2.length; _i++) {
                    var item = items_2[_i];
                    var currentPath = __spreadArray(__spreadArray([], path, true), [String(item.key)], false);
                    if (item === target) {
                        return currentPath;
                    }
                    if (item.children) {
                        var result = findPath_1(item.children, target, currentPath);
                        if (result)
                            return result;
                    }
                }
                return null;
            };
            var keyPath = findPath_1(menuItems, matchingItem);
            return keyPath || [String(matchingItem.key)];
        }
        // 如果是根路径，返回第一个菜单项的key
        if (path === "/" && ((_a = menuItems[0]) === null || _a === void 0 ? void 0 : _a.key)) {
            return [String(menuItems[0].key)];
        }
        return [];
    }, [location.pathname, menuItems, findMatchingMenuItem]);
    // 将菜单项转换为Ant Design Menu组件需要的items格式
    var convertToMenuItems = useCallback(function (items) {
        if (!items || items.length === 0) {
            return [];
        }
        return items.map(function (item) {
            var menuItem = {
                key: String(item.key),
                label: item.label,
                icon: item.icon,
            };
            // 递归处理子菜单
            if (item.children && item.children.length > 0) {
                menuItem.children = convertToMenuItems(item.children);
            }
            return menuItem;
        });
    }, []);
    var menuItemsForAntd = useMemo(function () { return convertToMenuItems(menuItems || []); }, [menuItems, convertToMenuItems]);
    // 递归查找菜单项（包括子菜单）
    var findMenuItemByKey = useCallback(function (items, targetKey) {
        for (var _i = 0, items_3 = items; _i < items_3.length; _i++) {
            var item = items_3[_i];
            if (String(item.key) === String(targetKey)) {
                return item;
            }
            if (item.children && item.children.length > 0) {
                var childMatch = findMenuItemByKey(item.children, targetKey);
                if (childMatch) {
                    return childMatch;
                }
            }
        }
        return null;
    }, []);
    // 处理菜单点击事件
    var handleMenuClick = useCallback(function (_a) {
        var key = _a.key;
        if (!menuItems || !Array.isArray(menuItems) || menuItems.length === 0) {
            return;
        }
        var menuItem = findMenuItemByKey(menuItems, key);
        if (menuItem) {
            // 优先使用 onClick
            if (menuItem.onClick) {
                menuItem.onClick();
            }
            else if (menuItem.path) {
                // 如果没有 onClick，使用 path 进行导航
                try {
                    navigate(menuItem.path, { replace: false });
                }
                catch (error) {
                    console.error("[MainLayout] navigate 调用失败:", error);
                    window.location.href = menuItem.path;
                }
            }
        }
    }, [menuItems, findMenuItemByKey, navigate]);
    // 自动生成面包屑（如果未提供）
    var breadcrumbs = useMemo(function () {
        if (providedBreadcrumbs && providedBreadcrumbs.length > 0) {
            return providedBreadcrumbs;
        }
        // 自动生成面包屑
        var path = location.pathname;
        var breadcrumbItems = [
            {
                title: "首页",
                href: "/",
            },
        ];
        // 从菜单项中找到当前路径对应的项
        var currentMenuItem = findMatchingMenuItem(menuItems || [], path);
        if (currentMenuItem) {
            breadcrumbItems.push({
                title: String(currentMenuItem.label),
                href: currentMenuItem.path,
            });
        }
        return breadcrumbItems;
    }, [providedBreadcrumbs, location.pathname, menuItems, findMatchingMenuItem]);
    // 处理鼠标按下事件
    var handleMouseDown = useCallback(function (e) {
        e.preventDefault();
        e.stopPropagation();
        isResizingRef.current = true;
        setIsResizing(true);
        startXRef.current = e.clientX;
        startWidthRef.current = siderWidth;
        document.body.style.cursor = "col-resize";
        document.body.style.userSelect = "none";
    }, [siderWidth]);
    // 处理鼠标移动事件
    var handleMouseMove = useCallback(function (e) {
        if (!isResizingRef.current)
            return;
        e.preventDefault();
        var deltaX = e.clientX - startXRef.current;
        var newWidth = Math.max(MIN_SIDER_WIDTH, Math.min(MAX_SIDER_WIDTH, startWidthRef.current + deltaX));
        setSiderWidth(newWidth);
    }, []);
    // 处理鼠标释放事件
    var handleMouseUp = useCallback(function () {
        if (!isResizingRef.current)
            return;
        isResizingRef.current = false;
        setIsResizing(false);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
    }, []);
    // 添加和移除事件监听器
    useEffect(function () {
        if (isResizing) {
            document.addEventListener("mousemove", handleMouseMove);
            document.addEventListener("mouseup", handleMouseUp);
        }
        return function () {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
        };
    }, [isResizing, handleMouseMove, handleMouseUp]);
    // 样式配置
    var siderBackground = (styles === null || styles === void 0 ? void 0 : styles.siderBackground) || "#f0f1f2";
    var headerBackground = (styles === null || styles === void 0 ? void 0 : styles.headerBackground) || "#fff";
    var contentBackground = (styles === null || styles === void 0 ? void 0 : styles.contentBackground) || "#fff";
    return (_jsxs(Layout, __assign({ style: { height: "100vh", overflow: "hidden" } }, { children: [_jsxs(Sider, __assign({ theme: "light", style: {
                    background: siderBackground,
                    borderRight: COMPONENT_OUTLINE_BORDER,
                    position: "relative",
                    overflow: "hidden",
                }, width: siderWidth }, { children: [_jsx("div", __assign({ style: {
                            width: "100%",
                            height: "64px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            borderBottom: COMPONENT_OUTLINE_BORDER,
                            background: siderBackground,
                        } }, { children: system.logo ? (_jsx("img", { src: system.logo, alt: system.logoAlt || system.name, style: { maxHeight: "40px", maxWidth: "80%" } })) : (_jsx("span", __assign({ style: {
                                fontSize: "20px",
                                fontWeight: "600",
                                color: "#1f1f1f",
                                textAlign: "center",
                            } }, { children: system.shortName || system.name }))) })), menuItemsForAntd.length === 0 ? (_jsx("div", __assign({ style: { padding: "20px", textAlign: "center", color: "#999" } }, { children: _jsx("div", { children: "\u26A0\uFE0F \u83DC\u5355\u9879\u4E3A\u7A7A" }) }))) : (_jsx(Menu, { theme: "light", mode: "inline", style: {
                            background: siderBackground,
                            border: "none",
                            height: "calc(100vh - 64px)",
                            overflow: "auto",
                        }, selectedKeys: getSelectedMenuKeys, items: menuItemsForAntd, onClick: handleMenuClick })), _jsx("div", { style: {
                            position: "absolute",
                            top: 0,
                            right: 0,
                            width: "4px",
                            height: "100%",
                            background: isResizing
                                ? (styles === null || styles === void 0 ? void 0 : styles.primaryColor) || "#1890ff"
                                : "transparent",
                            cursor: "col-resize",
                            zIndex: 10,
                            transition: "background-color 0.2s",
                        }, onMouseDown: handleMouseDown })] })), _jsxs(Layout, __assign({ style: { height: "100vh", overflow: "hidden" } }, { children: [_jsxs("div", __assign({ style: {
                            background: headerBackground,
                            borderBottom: COMPONENT_SPLIT_BORDER_BOTTOM,
                            padding: "0 24px",
                            height: "64px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            flexShrink: 0,
                            gap: 16,
                        } }, { children: [_jsx("div", __assign({ style: { minWidth: 0, flex: 1 } }, { children: breadcrumbs && breadcrumbs.length > 0 && (_jsx(Breadcrumb, { items: breadcrumbs.map(function (item) { return ({
                                        title: item.href ? (_jsx("a", __assign({ href: item.href, onClick: function (e) {
                                                e.preventDefault();
                                                navigate(item.href);
                                            }, style: { color: (styles === null || styles === void 0 ? void 0 : styles.primaryColor) || "#1890ff" } }, { children: item.title }))) : (item.title),
                                    }); }) })) })), (headerActions && headerActions.length > 0) || headerExtra ? (_jsxs(Space, __assign({ size: "middle", style: { flexShrink: 0 } }, { children: [headerActions === null || headerActions === void 0 ? void 0 : headerActions.map(function (a) { return (_jsx(Button, __assign({ type: "text", icon: a.icon, onClick: a.onClick }, { children: a.label }), a.key)); }), headerExtra] }))) : null] })), _jsx(Content, __assign({ style: {
                            padding: "16px",
                            background: contentBackground,
                            height: "calc(100vh - 64px)",
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                        } }, { children: _jsx("div", __assign({ style: {
                                flex: 1,
                                minHeight: 0,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "auto",
                            } }, { children: children || _jsxs("div", { children: ["\u6B22\u8FCE\u4F7F\u7528 ", system.name] }) })) }))] }))] })));
};
export default MainLayout;
